import { Link } from 'react-router-dom'
import { LandingNav } from '@/components/landing'

export function TermsPage() {
  return (
    <div className="min-h-screen bg-primary-bg">
      <LandingNav />
      <div className="max-w-3xl mx-auto px-4 py-16">
        <h1 className="text-3xl font-medium mb-2">Terms of Service</h1>
        <p className="text-sm text-secondary-text mb-12">Last updated: February 2, 2026</p>

        <div className="space-y-8 text-sm text-secondary-text leading-relaxed">
          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">1. Agreement to Terms</h2>
            <p>
              By accessing or using Beacon ("the Service"), operated by Beacon AI ("we", "us", "our"),
              you agree to be bound by these Terms of Service. If you do not agree, do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">2. Description of Service</h2>
            <p>
              Beacon is an AI-powered content generation and scheduling platform for social media.
              The Service analyzes your existing social media content to learn your writing style
              and generates new content matching that style. The Service integrates with third-party
              platforms including X (formerly Twitter) to publish content on your behalf.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">3. Account Registration</h2>
            <p>
              You must create an account to use the Service. You are responsible for maintaining the
              security of your account credentials and for all activity under your account. You must
              provide accurate information and be at least 18 years old. We reserve the right to
              suspend or terminate accounts that violate these terms.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">4. Third-Party Platform Access</h2>
            <p>
              The Service requires you to connect your X (Twitter) account via OAuth. By connecting
              your account, you authorize us to access your public posts for voice analysis and to
              publish content on your behalf when you approve it. You may disconnect your account at
              any time. We do not store your X password.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">5. AI-Generated Content</h2>
            <p>
              Content generated by the Service is created using artificial intelligence. You are solely
              responsible for reviewing, editing, and approving all content before it is posted to your
              social media accounts. We do not guarantee the accuracy, quality, or appropriateness of
              generated content. You retain full responsibility for any content posted through the Service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">6. Acceptable Use</h2>
            <p className="mb-2">You agree not to use the Service to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Generate spam, misleading, or deceptive content</li>
              <li>Impersonate others or misrepresent your identity</li>
              <li>Generate content that is hateful, harassing, or promotes violence</li>
              <li>Violate any applicable laws or third-party platform terms of service</li>
              <li>Attempt to reverse-engineer, exploit, or circumvent usage limits</li>
              <li>Resell or redistribute access to the Service without authorization</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">7. Subscription and Payments</h2>
            <p>
              The Service offers free and paid subscription plans. Paid plans are billed monthly through
              Stripe. By subscribing, you authorize recurring charges to your payment method. You may
              cancel at any time; access continues through the end of the billing period. Refunds are
              not provided for partial billing periods. We reserve the right to change pricing with
              30 days notice.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">8. Usage Limits</h2>
            <p>
              Each plan includes a specified number of content generations per billing period. Unused
              generations do not carry over. We may enforce rate limits to ensure service quality for
              all users.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">9. Intellectual Property</h2>
            <p>
              You retain ownership of content you create, including content generated with the
              assistance of the Service. The Service, including its software, design, and branding,
              is owned by Beacon AI and protected by applicable intellectual property laws. You may
              not copy, modify, or distribute any part of the Service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">10. Limitation of Liability</h2>
            <p>
              The Service is provided "as is" without warranties of any kind. We are not liable for
              any damages arising from your use of the Service, including but not limited to: content
              posted to your social media accounts, loss of data, service interruptions, or third-party
              platform actions taken against your account. Our total liability is limited to the amount
              you paid for the Service in the 12 months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">11. Termination</h2>
            <p>
              We may suspend or terminate your access to the Service at any time for violation of
              these terms or for any other reason with reasonable notice. Upon termination, your
              right to use the Service ceases immediately. Provisions that by their nature should
              survive termination will survive.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">12. Changes to Terms</h2>
            <p>
              We may update these terms from time to time. We will notify you of material changes
              via email or through the Service. Continued use after changes take effect constitutes
              acceptance of the updated terms.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">13. Contact</h2>
            <p>
              If you have questions about these terms, contact us at{' '}
              <a href="mailto:support@globalringai.com" className="text-primary-text hover:underline">
                support@globalringai.com
              </a>.
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-border flex items-center justify-between">
          <Link to="/" className="text-sm text-secondary-text hover:text-primary-text transition-colors">
            &larr; Back to home
          </Link>
          <Link to="/privacy" className="text-sm text-secondary-text hover:text-primary-text transition-colors">
            Privacy Policy &rarr;
          </Link>
        </div>
      </div>
    </div>
  )
}
